import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TableIconButton,
  TableIconLink,
} from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, SortState } from '../../components/SortableTh';
import {
  Badge,
  BanIcon,
  Button,
  Callout,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SegmentedControl,
  TextInput,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { ShipmentFileListItem } from '../../types/shipment';
import { formatDate, formatMoney } from '../../utils/format';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SERVER_SORT_KEYS = new Set([
  'createdAt',
  'fileNo',
  'customer',
  'arrivalDate',
  'status',
]);

type StatusFilter = 'all' | 'open' | 'invoiced';
const PILL_ICON_CLASS = 'h-3.5 w-3.5';

function statusPills() {
  const filters = t().shipmentFiles.filters;
  return [
    {
      id: 'all' as const,
      label: filters.all,
      icon: <FolderIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'open' as const,
      label: filters.open,
      icon: <DocumentTextIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'invoiced' as const,
      label: filters.invoiced,
      icon: <DocumentTextIcon className={PILL_ICON_CLASS} />,
    },
  ];
}

const sortGetters: Record<
  string,
  (item: ShipmentFileListItem) => string | number | boolean | null | undefined
> = {
  createdAt: (i) => i.createdAt,
  fileNo: (i) => i.fileNo,
  customer: (i) => i.customer.name,
  arrivalDate: (i) => i.arrivalDate,
  status: (i) => i.status,
  billable: (i) => i.totals.billableGross,
};

export function ShipmentFileListPage() {
  const labels = t().shipmentFiles;
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<ShipmentFileListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') ?? '',
  );

  const search = searchParams.get('search') ?? '';
  const status = (searchParams.get('status') as StatusFilter) || 'all';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.min(
    100,
    Math.max(1, Number(searchParams.get('pageSize')) || 20),
  );
  const sortBy = searchParams.get('sortBy') || 'createdAt';
  const sortDir =
    searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const sort: SortState = { key: sortBy, direction: sortDir };

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const displayItems = useMemo(() => {
    if (SERVER_SORT_KEYS.has(sort.key)) return items;
    return sortItems(items, sort, sortGetters);
  }, [items, sort]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getShipmentFiles({
          search: search || undefined,
          status: status === 'all' ? undefined : status.toUpperCase(),
          page,
          pageSize,
          sortBy: SERVER_SORT_KEYS.has(sortBy) ? sortBy : 'createdAt',
          sortDir,
        });
        if (cancelled) return;
        setItems(result.data);
        setTotal(result.meta.total);
        setPageCount(result.meta.pageCount);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [search, status, page, pageSize, sortBy, sortDir]);

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next);
  }

  function toggleSort(key: string) {
    if (sort.key === key) {
      updateParams({
        sortDir: sort.direction === 'asc' ? 'desc' : 'asc',
        page: '1',
      });
    } else {
      updateParams({ sortBy: key, sortDir: 'asc', page: '1' });
    }
  }

  async function handleDelete(item: ShipmentFileListItem) {
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteShipmentFile(item.id);
      setMessage(labels.softDeleteSuccess);
      setItems((prev) => prev.filter((row) => row.id !== item.id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <Button
            type="button"
            size="sm"
            onClick={() => navigate('/shipment-files/new')}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {labels.new}
          </Button>
        }
      >
        <FilterField wide label={t().common.search}>
          <TextInput
            type="search"
            value={searchInput}
            showClearButton={Boolean(searchInput)}
            size="sm"
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateParams({
                  search: searchInput.trim() || null,
                  page: '1',
                });
              }
            }}
          />
        </FilterField>
      </ListFiltersBar>

      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
        <SegmentedControl
          value={status}
          onChange={(value) =>
            updateParams({
              status: value === 'all' ? null : value,
              page: '1',
            })
          }
          size="sm"
          aria-label={labels.status}
          options={statusPills()}
        />
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState
            title={labels.emptyTitle}
            hint={labels.emptyHint}
          />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh sortKey="fileNo" sort={sort} onSort={toggleSort}>
                  {labels.fileNo}
                </SortableTh>
                <SortableTh sortKey="customer" sort={sort} onSort={toggleSort}>
                  {labels.customer}
                </SortableTh>
                <th className="px-3 py-2 font-medium">
                  {labels.containersSection}
                </th>
                <SortableTh sortKey="status" sort={sort} onSort={toggleSort}>
                  {labels.status}
                </SortableTh>
                <SortableTh
                  sortKey="billable"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.billableTotal}
                </SortableTh>
                <SortableTh
                  sortKey="arrivalDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.arrivalDate}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item) => (
                <tr key={item.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconLink
                        to={`/shipment-files/${item.id}`}
                        label={labels.view}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </TableIconLink>
                      {item.status === 'OPEN' ? (
                        <TableIconLink
                          to={`/shipment-files/${item.id}/edit`}
                          label={labels.editAction}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </TableIconLink>
                      ) : null}
                      {isAdmin && item.status === 'OPEN' ? (
                        <TableIconButton
                          label={labels.softDelete}
                          variant="muted"
                          onClick={() => void handleDelete(item)}
                        >
                          <BanIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to={`/shipment-files/${item.id}`}
                      className="text-brand hover:underline"
                    >
                      {item.fileNo}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{item.customer.name}</td>
                  <td className="px-3 py-2">
                    {item.containers.map((c) => c.containerNo).join(', ') ||
                      '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        item.status === 'INVOICED' ? 'success' : 'secondary'
                      }
                    >
                      {labels.statuses[item.status]}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(item.totals.billableGross)}
                  </td>
                  <td className="px-3 py-2">
                    {item.arrivalDate ? formatDate(item.arrivalDate) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTablePanel>

      <ListPagination
        page={page}
        totalPages={pageCount}
        total={total}
        itemsPerPage={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(next) => updateParams({ page: String(next) })}
        onItemsPerPageChange={(next) =>
          updateParams({ pageSize: String(next), page: '1' })
        }
      />
    </PageShell>
  );
}
